import React, { useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import ApiService from '../services/api';
import Autocomplete from '../components/ui/Autocomplete';
import { Download, Filter, RefreshCw, ChevronLeft, ChevronRight, Phone, Clock, User } from 'lucide-react';

interface Agent {
  id: number;
  name: string;
  email: string;
  username: string;
}

interface CallLog {
  id: number;
  agent_id: number;
  agent_name: string;
  agent_email: string;
  phone_number: string;
  contact_name: string | null;
  start_time: string;
  end_time: string | null;
  duration: number | null;
  status: string;
  notes: string | null;
}

interface CallListReport {
  data: CallLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

const today = new Date();
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const formatDateInput = (date: Date) => date.toISOString().slice(0, 10);

const AgentCallListReport: React.FC = () => {
  const { token } = useAuth();
  const [report, setReport] = useState<CallListReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(formatDateInput(firstDayOfMonth));
  const [dateTo, setDateTo] = useState(formatDateInput(today));
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agentSearchValue, setAgentSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);

  const searchAgents = async (query: string): Promise<Agent[]> => {
    if (!token) return [];

    try {
      const response = await ApiService.get<Agent[]>('agent-performance-report/search-agents', { query });
      return response.data;
    } catch (error) {
      console.error('Error searching agents:', error);
      return [];
    }
  };

  const handleAgentSelect = (option: any) => {
    if (option) {
      setSelectedAgent({
        id: option.id,
        name: option.name,
        email: option.email,
        username: option.username || ''
      });
    } else {
      setSelectedAgent(null);
    }
  };

  const fetchReport = async (page: number = 1) => {
    if (!token) return;

    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const params: Record<string, any> = {
        limit,
        offset
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedAgent) params.agent_id = selectedAgent.id;

      const response = await ApiService.get<CallListReport>('agent-performance-report/agent-call-list', params);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching agent call list report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'csv' = 'csv') => {
    if (!token) return;

    try {
      const params: Record<string, any> = { format };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedAgent) params.agent_id = selectedAgent.id;

      const response = await ApiService.get('agent-performance-report/export-call-report', params);
      const data = response.data as { data: any[]; filename: string; format: string };

      if (format === 'csv') {
        const csvContent = data.data.map((row: any[]) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    fetchReport(currentPage);
  }, [token, dateFrom, dateTo, selectedAgent, currentPage]);

  const formatDuration = (seconds: number | null) => {
    if (!seconds || seconds === 0) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100 dark:bg-green-900';
      case 'missed':
        return 'text-red-600 bg-red-100 dark:bg-red-900';
      case 'rejected':
        return 'text-orange-600 bg-orange-100 dark:bg-orange-900';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900';
    }
  };

  const totalPages = report ? Math.ceil(report.pagination.total / limit) : 0;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Agent Call List Report
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Detailed call list with start time, end time, duration, and status
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full lg:w-auto">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="From Date"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="To Date"
                />
                <Autocomplete<Agent>
                  placeholder="Search agents..."
                  value={agentSearchValue}
                  onChange={setAgentSearchValue}
                  onSelect={(agent) => setSelectedAgent(agent)}
                  searchFunction={searchAgents}
                  debounceMs={300}
                  className="w-full"
                  style={{ minWidth: '200px' }}
                  displayKey="name"
                  subDisplayKey="email"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full lg:w-auto">
              <Button onClick={() => fetchReport(currentPage)} disabled={loading} className="flex items-center gap-2 w-full sm:w-auto">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button onClick={() => exportReport('csv')} className="flex items-center gap-2 w-full sm:w-auto">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {report && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Phone className="w-8 h-8 text-blue-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Calls</h3>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.pagination.total}</div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <User className="w-8 h-8 text-green-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Showing</h3>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.data.length}</div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Clock className="w-8 h-8 text-purple-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Page</h3>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{currentPage} / {totalPages}</div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Filter className="w-8 h-8 text-orange-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Per Page</h3>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{limit}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Report Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Agent Call List Report</h2>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
                Loading call data...
              </div>
            ) : report && report.data.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Phone Number</TableHead>
                      <TableHead>Contact Name</TableHead>
                      <TableHead>Start Time</TableHead>
                      <TableHead>End Time</TableHead>
                      <TableHead>Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.data.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{call.agent_name}</div>
                            <div className="text-sm text-gray-500">{call.agent_email}</div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">{call.phone_number}</TableCell>
                        <TableCell>{call.contact_name || '-'}</TableCell>
                        <TableCell>{formatDateTime(call.start_time)}</TableCell>
                        <TableCell>
                          {call.end_time ? formatDateTime(call.end_time) : '-'}
                        </TableCell>
                        <TableCell>{formatDuration(call.duration)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, report.pagination.total)} of {report.pagination.total} results
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          variant="outline"
                          size="sm"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No call data available for the selected filters.</div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AgentCallListReport;
